import React, { useState } from 'react';
import { WheelSafeNumberInput } from '../common/WheelSafeNumberInput';
import {
    Edit,
    SimpleForm,
    TextInput,
    BooleanInput,
    SelectInput,
    ReferenceInput,
    AutocompleteInput,
    required,
    minValue,
    maxValue,
    Toolbar,
    useRecordContext,
    useDelete,
    useNotify,
    useRedirect,
    useTranslate,
    useGetIdentity,
    FormDataConsumer,
    useInput,
} from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { useEffect } from 'react';
import { 
    Box, 
    Typography, 
    Card, 
    CardContent, 
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Alert,
    IconButton,
} from '@mui/material';
import { 
    Business, 
    Phone, 
    Security, 
    BatteryChargingFull, 
    Info, 
    Warning, 
    ArrowBack, 
    Delete,
    LocalParking,
    Photo,
    Schedule,
} from '@mui/icons-material';
import { MultiLanguageFields } from './MultiLanguageFields';
import { HongKongAddressFields } from './HongKongAddressFields';
import { OperatingHoursField } from './OperatingHoursField';
import { ImageUploadInput } from './ImageUploadInput';
import { MultiImageUploadInput } from './MultiImageUploadInput';
import { SiteValidationBanner } from './SiteValidationBanner';
import { SiteSaveButton } from './SiteSaveButton';
import { AmenitiesInput } from './AmenitiesInput';
import { SiteKioskSettingsSection } from './SiteKioskSettingsPanel';
import { OPERATING_ACCESS_OPTIONS } from '../../constants/operatingAccess';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';

const SITE_KNOWN_FIELDS = new Set([
    'name', 'vendorId', 'latitude', 'longitude',
    'addressLine1', 'addressLine2', 'postalCode', 'timezone',
    'phone', 'email', 'supportPhone', 'website',
    'accessType', 'isActive', 'parkingType', 'hasCanopy',
    'reservationEnabled', 'emailReceiptEnabled', 'appAccessEnabled', 'kioskAccessEnabled',
    'totalMaxPower', 'gridConnectionCapacity', 'operatingAccess',
    'parkingInfo', 'operatingHours', 'primaryImage', 'images',
    'amenities', 'tags', 'description',
]);

const mapSiteBackendPath = (path: string): string =>
    path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');

const siteFieldLabelKey = (field: string): string => {
    const head = field.split('.')[0];
    return SITE_KNOWN_FIELDS.has(head)
        ? `resources.sites.fields.${head}`
        : field;
};

const DeleteSiteSection = () => {
    const record = useRecordContext();
    const [open, setOpen] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    const notify = useNotify();
    const redirect = useRedirect();
    const [deleteOne] = useDelete();
    const translate = useTranslate();
    
    const handleClick = () => {
        setOpen(true);
        setConfirmText('');
    };
    
    const handleClose = () => {
        setOpen(false);
        setConfirmText('');
    };
    
    const handleConfirm = () => {
        if (confirmText === record?.name) {
            void deleteOne(
                'sites',
                { id: record.id, previousData: record },
                {
                    onSuccess: () => {
                        notify(translate('resources.sites.notifications.deleteSuccess'));
                        redirect('/sites');
                    },
                    onError: () => {
                        notify(translate('resources.sites.notifications.deleteError'), { type: 'error' });
                    },
                }
            );
            setOpen(false);
        }
    };
    
    const hasChargers = record?.chargerCount > 0;
    
    return (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 2, sm: 0 } }}>
            <Box>
                <Typography variant="body2" color="text.secondary">
                    {hasChargers
                        ? translate('resources.sites.edit.cannotDeleteWithChargers')
                        : translate('resources.sites.edit.deleteSiteDescription')}
                </Typography>
            </Box>
            <Button
                variant="outlined"
                onClick={handleClick}
                disabled={hasChargers}
                sx={{ 
                    borderColor: hasChargers ? '#BDBDBD' : '#F44336',
                    color: hasChargers ? '#BDBDBD' : '#F44336',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                    '&:hover': {
                        borderColor: hasChargers ? '#BDBDBD' : '#D32F2F',
                        backgroundColor: hasChargers ? 'transparent' : 'rgba(244,67,54,0.08)',
                    },
                    '&.Mui-disabled': {
                        borderColor: '#BDBDBD',
                        color: '#BDBDBD',
                    },
                }}
                title={hasChargers 
                    ? translate('resources.sites.edit.cannotDeleteWithChargers')
                    : translate('common.delete')}
            >
                {translate('common.delete')}
            </Button>
            
            <Dialog 
                open={open} 
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Warning sx={{ color: '#F44336' }} />
                        <Typography variant="h6">
                            {translate('resources.sites.edit.deleteConfirmTitle')}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>{translate('resources.sites.edit.deleteConfirmWarning')}</strong>
                        </Typography>
                        <Typography variant="body2">
                            {translate('resources.sites.edit.deleteConfirmMessage')} <strong>{record?.name}</strong>.
                            {' '}{translate('resources.sites.edit.deleteConfirmInfo')}
                        </Typography>
                    </Alert>
                    
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        {translate('resources.sites.edit.deleteConfirmPrompt')} <strong>{record?.name}</strong> {translate('resources.sites.edit.deleteConfirmPromptBelow')}
                    </Typography>
                    
                    <TextField
                        fullWidth
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder={translate('resources.sites.edit.deleteConfirmTypePlaceholder', { name: record?.name })}
                        variant="outlined"
                        autoFocus
                        error={confirmText !== '' && confirmText !== record?.name}
                        helperText={
                            confirmText !== '' && confirmText !== record?.name
                                ? translate('resources.sites.edit.nameDoesNotMatch')
                                : ''
                        }
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button 
                        onClick={handleClose}
                        sx={{ textTransform: 'none' }}
                    >
                        {translate('common.cancel')}
                    </Button>
                    <Button 
                        onClick={handleConfirm}
                        variant="contained"
                        disabled={confirmText !== record?.name}
                        sx={{
                            backgroundColor: '#F44336',
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: '#D32F2F',
                            },
                            '&.Mui-disabled': {
                                backgroundColor: '#FFCDD2',
                            },
                        }}
                    >
                        {translate('resources.sites.edit.deleteButton')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

const EditToolbar = () => {
    return (
        <Toolbar
            sx={{
                backgroundColor: '#FAFAFA',
                borderTop: '1px solid #E0E0E0',
                px: { xs: 2, sm: 3 },
                py: 2,
                mt: 4,
                '& .MuiButton-root': {
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                },
            }}
        >
            <SiteSaveButton />
        </Toolbar>
    );
};

const PageHeader = () => {
    const redirect = useRedirect();
    const record = useRecordContext();
    const translate = useTranslate();
    
    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.5, sm: 2 },
            mb: { xs: 2, sm: 4 },
        }}>
            <IconButton
                onClick={() => redirect('/sites')}
                size="large"
                sx={{
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:hover': {
                        backgroundColor: '#F5F5F5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    },
                }}
                title={translate('resources.sites.edit.backToList')}
            >
                <ArrowBack />
            </IconButton>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                    {translate('resources.sites.edit.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: { xs: '0.75rem', sm: '1rem' } }}>
                    {record?.name} • {record?.city}, {record?.state}
                </Typography>
            </Box>
        </Box>
    );
};

// Temporarily removed to fix infinite loop issue
// const ImageUploadFieldsWrapper = () => {
//     const { setValue, watch } = useFormContext();
//     const translate = useTranslate();
//     
//     const primaryImage = watch('primaryImage');
//     const images = watch('images');
//     
//     return (
//         <>
//             <ImageUploadField
//                 source="primaryImage"
//                 value={primaryImage}
//                 onChange={(value) => setValue('primaryImage', value)}
//                 label={translate('resources.sites.fields.primaryImage')}
//             />
//             
//             <MultiImageUploadField
//                 source="images"
//                 value={images}
//                 onChange={(value) => setValue('images', value)}
//                 label={translate('resources.sites.fields.images')}
//                 maxImages={10}
//             />
//         </>
//     );
// };


export const SiteEdit = () => {
    const { data: identity } = useGetIdentity();
    const actorVendorIds: string[] = ((identity as any)?.vendorIds as string[]) || [];
    const isVendorScoped = actorVendorIds.length > 0;
    const translate = useTranslate();
    const notify = useNotify();
    const redirect = useRedirect();
    const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
    const validateLatitude = [required(), minValue(-90), maxValue(90)];
    const validateLongitude = [required(), minValue(-180), maxValue(180)];
    const validateRequired = [required()];

    return (
        <Edit
            title={false}
            component="div"
            redirect="list"
            mutationMode="pessimistic"
            transform={(data: any) => {
                const { _tempRegion, vendor, ...rest } = data;
                // Extract vendorId from the nested vendor object if it exists
                const vendorId = vendor?.vendorId || null;
                // Return the data with vendorId at the root level
                return {
                    ...rest,
                    vendorId
                };
            }}
            mutationOptions={{
                onSuccess: () => {
                    setServerErrors({});
                    notify(translate('resources.sites.notifications.updateSuccess'), { type: 'success' });
                },
                onError: (error: any) => {
                    const apiError = error?.body || error;
                    const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
                    const fieldErrors: Record<string, string> = {};
                    const summaryLines: string[] = [];
                    if (rawErrors) {
                        for (const [path, msgs] of Object.entries(rawErrors)) {
                            const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
                            const translated = translateServerMessage(rawMsg, translate);
                            const field = mapSiteBackendPath(path);
                            fieldErrors[field] = translated;
                            const label = translate(siteFieldLabelKey(field), { _: field });
                            summaryLines.push(`• ${label}: ${translated}`);
                        }
                    }
                    setServerErrors(fieldErrors);

                    const headerKey = apiError?.errorCode
                        ? `errors.${apiError.errorCode}`
                        : 'validation.formHasErrors';
                    const header = translate(headerKey, { _: apiError?.message || 'Error' });

                    if (summaryLines.length > 0) {
                        notify(`${header}\n${summaryLines.join('\n')}`, {
                            type: 'error',
                            multiLine: true,
                            autoHideDuration: 10000,
                        });
                    } else {
                        notify(header, { type: 'error', autoHideDuration: 6000 });
                    }
                }
            }}
            sx={{
                backgroundColor: 'transparent',
                '& .RaEdit-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
        >
            <SimpleForm
                toolbar={<EditToolbar />}
                sx={{
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                    '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                        px: { xs: 0, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    },
                    padding: { xs: 0, sm: 'inherit' },
                }}
            >
                <ServerErrorBinder errors={serverErrors} />
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', '& .MuiFormControl-root': { width: '100%' } }}>
                    <PageHeader />

                    <SiteValidationBanner />

                    {/* Basic Information Section */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Business sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {translate('resources.sites.edit.basicInfo')}
                                </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextInput
                                    source="name"
                                    label={translate('resources.sites.fields.premisesName')}
                                    validate={validateRequired}
                                    fullWidth
                                />
                                <ReferenceInput
                                    source="vendor.vendorId"
                                    reference="vendors"
                                    allowEmpty
                                    filter={isVendorScoped ? { id: actorVendorIds } : undefined}
                                >
                                    <AutocompleteInput
                                        label={translate('resources.sites.fields.merchant')}
                                        optionText="name"
                                        fullWidth
                                        // Vendor-scoped admins cannot reassign a site's merchant
                                        // (backend enforces this with 403 — match the UX here).
                                        readOnly={isVendorScoped}
                                        helperText={
                                            isVendorScoped
                                                ? translate('resources.sites.edit.vendorHelperTextScoped', {
                                                      _: 'Restricted to your assigned merchants. Reassignment is disabled.',
                                                  })
                                                : translate('resources.sites.edit.vendorHelperText')
                                        }
                                        sx={{ '& .MuiAutocomplete-input': { minHeight: '23px' } }}
                                        emptyText={translate('resources.sites.edit.noMerchantAssigned')}
                                        clearOnBlur
                                    />
                                </ReferenceInput>
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <WheelSafeNumberInput 
                                            source="latitude" 
                                            validate={validateLatitude}
                                            fullWidth
                                            step={0.000001}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <WheelSafeNumberInput 
                                            source="longitude" 
                                            validate={validateLongitude}
                                            fullWidth
                                            step={0.000001}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                </Box>
                                <TextInput 
                                    source="addressLine1" 
                                    validate={validateRequired}
                                    fullWidth
                                />
                                <TextInput 
                                    source="addressLine2" 
                                    fullWidth
                                />
                                <FormDataConsumer>
                                    {({ formData }) => (
                                        <HongKongAddressFields
                                            countryValue={formData?.country}
                                            cityValue={formData?.city}
                                            stateValue={formData?.state}
                                            validateRequired={validateRequired}
                                        />
                                    )}
                                </FormDataConsumer>
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput 
                                            source="postalCode" 
                                            fullWidth
                                            helperText={translate('resources.sites.edit.postalCodeHelp')}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <SelectInput
                                            source="timezone"
                                            choices={[
                                                { id: 'Asia/Hong_Kong', name: 'Asia/Hong Kong' },
                                                { id: 'Asia/Shanghai', name: 'Asia/Shanghai' },
                                                { id: 'Asia/Taipei', name: 'Asia/Taipei' },
                                            ]}
                                            fullWidth
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                    
                    {/* Contact Information Section */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Phone sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {translate('resources.sites.edit.contactInfo')}
                                </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput 
                                            source="phone" 
                                            fullWidth
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput 
                                            source="email" 
                                            fullWidth
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput 
                                            source="supportPhone" 
                                            fullWidth
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput 
                                            source="website" 
                                            fullWidth
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                    
                    {/* Access & Status Section */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Security sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {translate('resources.sites.edit.accessStatus')}
                                </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                    <SelectInput
                                        source="accessType"
                                        choices={[
                                            { id: 'public', name: translate('resources.sites.accessTypes.public') },
                                            { id: 'private', name: translate('resources.sites.accessTypes.private') },
                                            { id: 'restricted', name: translate('resources.sites.accessTypes.restricted') },
                                        ]}
                                        fullWidth
                                        validate={validateRequired}
                                        sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                    />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', '& .MuiFormControl-root': { marginTop: 0 } }}>
                                    <BooleanInput 
                                        source="isActive" 
                                        label={translate('resources.sites.edit.activeSite')}
                                    />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                    
                    {/* Charger Information Section */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <BatteryChargingFull sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {translate('resources.sites.edit.chargerInfo')}
                                </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <WheelSafeNumberInput 
                                            source="chargerCount" 
                                            fullWidth
                                            min={0}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <WheelSafeNumberInput 
                                            source="availableChargers" 
                                            fullWidth
                                            min={0}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <WheelSafeNumberInput 
                                            source="totalMaxPower" 
                                            fullWidth
                                            min={0}
                                            step={0.1}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <WheelSafeNumberInput 
                                            source="gridConnectionCapacity" 
                                            fullWidth
                                            min={0}
                                            step={0.1}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                    
                    {/* Features Section */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <LocalParking sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {translate('resources.sites.edit.features')}
                                </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <SelectInput
                                            source="parkingType"
                                            choices={[
                                                { id: 'street', name: translate('resources.sites.parkingTypes.street') },
                                                { id: 'lot', name: translate('resources.sites.parkingTypes.lot') },
                                                { id: 'dedicated', name: translate('resources.sites.parkingTypes.dedicated') },
                                            ]}
                                            fullWidth
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', '& .MuiFormControl-root': { marginTop: 0 } }}>
                                        <BooleanInput 
                                            source="hasCanopy" 
                                            label={translate('resources.sites.fields.hasCanopy')}
                                        />
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', '& .MuiFormControl-root': { marginTop: 0 } }}>
                                    <BooleanInput
                                        source="reservationEnabled"
                                        label={translate('resources.sites.fields.reservationEnabled')}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', '& .MuiFormControl-root': { marginTop: 0 } }}>
                                    <BooleanInput
                                        source="emailReceiptEnabled"
                                        label={translate('resources.sites.fields.emailReceiptEnabled')}
                                        helperText={translate('resources.sites.create.emailReceiptHelperText')}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', '& .MuiFormControl-root': { marginTop: 0 } }}>
                                    <BooleanInput
                                        source="appAccessEnabled"
                                        label={translate('resources.sites.fields.appAccessEnabled')}
                                        helperText={translate('resources.sites.create.appAccessEnabledHelperText')}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', '& .MuiFormControl-root': { marginTop: 0 } }}>
                                    <BooleanInput
                                        source="kioskAccessEnabled"
                                        label={translate('resources.sites.fields.kioskAccessEnabled')}
                                        helperText={translate('resources.sites.create.kioskAccessEnabledHelperText')}
                                    />
                                </Box>
                                <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 3, mt: 1 }}>
                                    <AmenitiesInput />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    <SiteKioskSettingsSection />

                    {/* Operating Hours Section */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent>
                            <OperatingHoursField />
                            <Box sx={{ mt: 2 }}>
                                <SelectInput
                                    source="operatingAccess"
                                    label={translate('resources.sites.fields.operatingAccess')}
                                    choices={OPERATING_ACCESS_OPTIONS}
                                    defaultValue="24_7"
                                    fullWidth
                                    helperText={translate('resources.sites.helpers.operatingAccess')}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                    
                    {/* Parking & Restrictions Section */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <LocalParking sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {translate('resources.sites.edit.parkingRestrictions')}
                                </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                {/* Parking Info */}
                                <Box>
                                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                        {translate('resources.sites.fields.parkingInfo')}
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <WheelSafeNumberInput
                                            source="parkingInfo.weekdayRate"
                                            label={translate('resources.sites.fields.parkingWeekdayRate')}
                                            fullWidth
                                            helperText={translate('resources.sites.edit.parkingWeekdayRateHelp')}
                                            min={0}
                                        />
                                        <WheelSafeNumberInput
                                            source="parkingInfo.weekendRate"
                                            label={translate('resources.sites.fields.parkingWeekendRate')}
                                            fullWidth
                                            helperText={translate('resources.sites.edit.parkingWeekendRateHelp')}
                                            min={0}
                                        />
                                        <WheelSafeNumberInput
                                            source="parkingInfo.publicHolidayRate"
                                            label={translate('resources.sites.fields.parkingPublicHolidayRate')}
                                            fullWidth
                                            helperText={translate('resources.sites.edit.publicHolidayRateHelp')}
                                            min={0}
                                        />
                                        <WheelSafeNumberInput
                                            source="parkingInfo.freeMinutes"
                                            label={translate('resources.sites.fields.parkingFreeMinutes')}
                                            fullWidth
                                            helperText={translate('resources.sites.edit.freeMinutesHelp')}
                                            min={0}
                                            step={5}
                                        />
                                    </Box>
                                </Box>
                                
                                {/* Restrictions */}
                                <Box>
                                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                        {translate('resources.sites.fields.restrictions')}
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <TextInput 
                                            source="restrictions.heightLimit" 
                                            label={translate('resources.sites.fields.heightLimit')}
                                            fullWidth
                                            helperText={translate('resources.sites.helpers.heightLimit')}
                                        />
                                        <TextInput 
                                            source="restrictions.security" 
                                            label={translate('resources.sites.fields.security')}
                                            fullWidth
                                            defaultValue="24/7 CCTV"
                                            helperText={translate('resources.sites.helpers.security')}
                                        />
                                        <TextInput 
                                            source="restrictions.notes" 
                                            label={translate('resources.sites.fields.restrictionNotes')}
                                            fullWidth
                                            multiline
                                            rows={2}
                                            helperText={translate('resources.sites.helpers.restrictionNotes')}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                    
                    {/* Images Section */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Photo sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {translate('resources.sites.fields.images')}
                                </Typography>
                            </Box>
                            
                            <ImageUploadInput
                                source="primaryImage"
                                label={translate('resources.sites.fields.primaryImage')}
                                helperText={translate('resources.sites.helpers.primaryImage')}
                            />
                            <MultiImageUploadInput
                                source="images"
                                label={translate('resources.sites.fields.images')}
                                helperText={translate('resources.sites.helpers.images')}
                                maxImages={10}
                            />
                        </CardContent>
                    </Card>
                    
                    {/* Notes Section */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Info sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {translate('resources.sites.fields.notes')}
                                </Typography>
                            </Box>
                            
                            <Box sx={{ '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                <TextInput 
                                    source="notes" 
                                    fullWidth
                                    multiline
                                    rows={3}
                                    helperText={translate('resources.sites.edit.notesHelperText')}
                                    sx={{ '& .MuiInputBase-root': { minHeight: '120px' } }}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                    
                    {/* Multi-language Translations Section */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent>
                            <FormDataConsumer>
                                {({ formData }) => (
                                    <MultiLanguageFields 
                                        formData={formData}
                                        fields={[
                                            {
                                                name: 'name',
                                                label: translate('resources.sites.fields.name'),
                                                required: true,
                                                helperText: translate('resources.sites.multilang.nameHelperText')
                                            },
                                            {
                                                name: 'addressLine1',
                                                label: translate('resources.sites.fields.addressLine1'),
                                                required: true,
                                                helperText: translate('resources.sites.multilang.addressHelperText')
                                            },
                                            {
                                                name: 'addressLine2',
                                                label: translate('resources.sites.fields.addressLine2'),
                                            },
                                            {
                                                name: 'city',
                                                label: translate('resources.sites.fields.city'),
                                                required: true,
                                            },
                                            {
                                                name: 'state',
                                                label: translate('resources.sites.fields.state'),
                                                required: true,
                                            },
                                            {
                                                name: 'country',
                                                label: translate('resources.sites.fields.country'),
                                                required: true,
                                            },
                                            {
                                                name: 'notes',
                                                label: translate('resources.sites.fields.notes'),
                                                multiline: true,
                                                rows: 3,
                                                helperText: translate('resources.sites.multilang.notesHelperText')
                                            },
                                            {
                                                name: 'pricingInfo',
                                                label: translate('resources.sites.fields.pricingInfo'),
                                                multiline: true,
                                                rows: 3,
                                                helperText: translate('resources.sites.multilang.pricingInfoHelperText')
                                            }
                                        ]}
                                    />
                                )}
                            </FormDataConsumer>
                        </CardContent>
                    </Card>
                    
                    {/* Additional Information Section */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Info sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {translate('resources.sites.edit.additionalInfo')}
                                </Typography>
                            </Box>
                            
                            <Box sx={{ '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                <TextInput 
                                    source="qrCode" 
                                    fullWidth
                                    sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                    
                    {/* Delete Site Section */}
                    <Card sx={{ mt: 3, overflow: 'hidden' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Delete sx={{ mr: 2, color: '#F44336' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {translate('resources.sites.edit.deleteSite')}
                                </Typography>
                            </Box>
                            
                            <DeleteSiteSection />
                        </CardContent>
                    </Card>
                </Box>
            </SimpleForm>
        </Edit>
    );
};